import * as THREE from "three";
import { kart } from "@/components/telemetry/onboard/kart";

const M = 111320;
const TRACK_W = 8;

export function llToXY(ll, origin) {
  const k = M * Math.cos((origin[0] * Math.PI) / 180);
  return ll.map(([la, lo]) => [(lo - origin[1]) * k, (la - origin[0]) * M]);
}

export function localXY(lap, origin) {
  if (lap.lat?.length && origin) {
    const k = M * Math.cos((origin[0] * Math.PI) / 180);
    return lap.lat.map((la, i) => [(lap.lon[i] - origin[1]) * k, (la - origin[0]) * M]);
  }
  const xs = lap.x, ys = lap.y;
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return xs.map((x, i) => [x - cx, ys[i] - cy]);
}

const v3 = ([e, n], h = 0) => new THREE.Vector3(e, h, -n);

function smoothLine(pts, step = 0.4) {
  const avg = pts.map((_, i) => {
    const w = pts.slice(Math.max(0, i - 2), i + 3);
    return v3([w.reduce((s, p) => s + p[0], 0) / w.length, w.reduce((s, p) => s + p[1], 0) / w.length]);
  });
  const curve = new THREE.CatmullRomCurve3(avg, false, "centripetal");
  return curve.getSpacedPoints(Math.max(2, Math.round(curve.getLength() / step))).map((v) => [v.x, -v.z]);
}

function noiseTex(rgb, spread, repeat, size = 256) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  const img = g.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    const n = (Math.random() - 0.5) * spread;
    img.data.set([rgb[0] + n, rgb[1] + n, rgb[2] + n * 0.8, 255], i * 4);
  }
  g.putImageData(img, 0, 0);
  const tx = new THREE.CanvasTexture(c);
  tx.wrapS = tx.wrapT = THREE.RepeatWrapping;
  tx.repeat.set(repeat[0], repeat[1]);
  tx.colorSpace = THREE.SRGBColorSpace;
  tx.anisotropy = 8;
  return tx;
}

function skyTex() {
  const c = document.createElement("canvas");
  c.width = 2;
  c.height = 256;
  const g = c.getContext("2d");
  const gr = g.createLinearGradient(0, 0, 0, 256);
  gr.addColorStop(0, "#3d74c4");
  gr.addColorStop(0.55, "#9cc3ea");
  gr.addColorStop(1, "#dde9f2");
  g.fillStyle = gr;
  g.fillRect(0, 0, 2, 256);
  const tx = new THREE.CanvasTexture(c);
  tx.colorSpace = THREE.SRGBColorSpace;
  return tx;
}

function strip(pts, offA, offB, h, material, colorAt) {
  const pos = [], col = [], uv = [];
  const c = new THREE.Color();
  let dist = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
    let tx = b[0] - a[0], ty = b[1] - a[1];
    const L = Math.hypot(tx, ty) || 1;
    tx /= L; ty /= L;
    if (i > 0) dist += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    pos.push(pts[i][0] - ty * offA, h, -(pts[i][1] + tx * offA), pts[i][0] - ty * offB, h, -(pts[i][1] + tx * offB));
    c.set(colorAt ? colorAt(i) : 0xffffff);
    col.push(c.r, c.g, c.b, c.r, c.g, c.b);
    uv.push(0, dist / 8, 1, dist / 8);
  }
  const idx = [];
  for (let i = 0; i < pts.length - 1; i++) idx.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  const m = new THREE.Mesh(g, material);
  m.receiveShadow = true;
  return m;
}

function trees(scene, xy) {
  const geo = new THREE.ConeGeometry(1, 1, 7);
  const inst = new THREE.InstancedMesh(geo, new THREE.MeshStandardMaterial({ color: 0x24502c, roughness: 0.95 }), 260);
  const xs = xy.map((p) => p[0]), ys = xy.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs) - 120, Math.max(...xs) + 120, Math.min(...ys) - 120, Math.max(...ys) + 120];
  const m = new THREE.Matrix4(), q = new THREE.Quaternion();
  let n = 0;
  for (let tries = 0; tries < 3000 && n < 260; tries++) {
    const p = [x0 + Math.random() * (x1 - x0), y0 + Math.random() * (y1 - y0)];
    if (xy.some((t, i) => i % 3 === 0 && Math.hypot(t[0] - p[0], t[1] - p[1]) < 22)) continue;
    const h = 6 + Math.random() * 9;
    m.compose(v3(p, h / 2), q, new THREE.Vector3(h * 0.28, h, h * 0.28));
    inst.setMatrixAt(n++, m);
  }
  inst.count = n;
  scene.add(inst);
}

const box = (w, h, d, mat, x, y, z) => {
  const o = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  o.position.set(x, y, z);
  return o;
};

const onLayer = (o, l) => o.traverse((c) => c.layers.set(l));

export function buildScene(xyA, xyB, renderer) {
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  const scene = new THREE.Scene();
  scene.background = skyTex();
  scene.fog = new THREE.Fog(0xcfdfe9, 90, 650);
  scene.add(new THREE.HemisphereLight(0xcfe3ff, 0x40602c, 0.9));
  const sun = new THREE.DirectionalLight(0xfff1dc, 1.6);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.layers.enable(1);
  sun.shadow.camera.layers.enable(2);
  Object.assign(sun.shadow.camera, { left: -12, right: 12, top: 12, bottom: -12, near: 1, far: 120 });
  scene.add(sun, sun.target);
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(4000, 4000), new THREE.MeshStandardMaterial({ map: noiseTex([62, 104, 52], 34, [500, 500]), roughness: 1 }));
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.3;
  ground.receiveShadow = true;
  scene.add(ground);
  const half = TRACK_W / 2;
  const asphalt = new THREE.MeshStandardMaterial({ map: noiseTex([78, 80, 86], 30, [1, 1]), roughness: 0.92, side: THREE.DoubleSide });
  const kerbMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, side: THREE.DoubleSide });
  const lineMat = new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.7, side: THREE.DoubleSide });
  const kerb = (i) => (Math.floor(i / 2) % 2 ? 0xf5f5f5 : 0xd62828);
  scene.add(strip(xyA, -half, half, 0, asphalt));
  scene.add(strip(xyA, half - 0.25, half - 0.1, 0.012, lineMat), strip(xyA, -half + 0.1, -half + 0.25, 0.012, lineMat));
  scene.add(strip(xyA, half, half + 1.0, 0.02, kerbMat, kerb), strip(xyA, -half - 1.0, -half, 0.02, kerbMat, kerb));
  [[xyA, 0x3b82f6, 0.03], [xyB, 0xff3b30, 0.036]].forEach(([pts, color, h]) => {
    if (pts) scene.add(strip(smoothLine(pts), -0.1, 0.1, h, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false })));
  });
  const start = box(TRACK_W, 0.02, 0.6, new THREE.MeshBasicMaterial({ color: 0xffffff }), 0, 0, 0);
  start.position.copy(v3(xyA[0], 0.03));
  const d = v3(xyA[2]).sub(v3(xyA[0]));
  start.rotation.y = Math.atan2(d.x, d.z);
  scene.add(start);
  trees(scene, xyA);
  const spec = [[0x2f6fe0, "A"], [0xe1261c, "B"]].slice(0, xyB ? 2 : 1);
  const karts = spec.map(([c, l], i) => { const k = kart(c, l); onLayer(k, i + 1); scene.add(k); return k; });
  const ghosts = xyB ? spec.map(([c, l], i) => { const k = kart(c, l, true); onLayer(k, 2 - i); scene.add(k); return k; }) : [];
  return { scene, karts, ghosts, sun };
}

export function placeKart(k, p, ahead) {
  k.position.copy(v3(p));
  const d = v3(ahead).sub(v3(p));
  if (d.lengthSq() > 0.0001) k.rotation.y = Math.atan2(d.x, d.z) + Math.PI;
}

export function placeCamera(cam, p, ahead, mode, dt) {
  const pos = v3(p), fwd = v3(ahead).sub(pos);
  if (fwd.lengthSq() < 0.0001) return;
  fwd.normalize();
  const onb = mode === "onboard";
  const want = pos.clone().addScaledVector(fwd, onb ? -0.2 : -8).setY(onb ? 1.12 : 3.6);
  const look = pos.clone().addScaledVector(fwd, onb ? 20 : 6).setY(onb ? 0.75 : 0.5);
  const u = cam.userData;
  const keep = u.pos && u.mode === mode && dt < 0.5;
  u.pos = keep ? u.pos.lerp(want, 1 - Math.exp(-dt * (onb ? 16 : 6))) : want.clone();
  u.look = keep ? u.look.lerp(look, 1 - Math.exp(-dt * 7)) : look.clone();
  u.mode = mode;
  cam.position.copy(u.pos);
  cam.lookAt(u.look);
}
